import { set, update } from 'firebase/database'

import {
  getApiActivitiesWall,
  postApiActivitiesRandomNumber,
  postApiActivitiesWall,
} from '../api/client'
import { ref, rtdb } from '.'
import { getRTDB, listenToRTDB } from './utils'

export interface RandomNumberWinner {
  guestId: string
  name: string
  number: number
  timestamp: number // Changed to number for RTDB compatibility
}

export interface RandomNumberLog {
  guestId: string
  name: string
  number: number
  timestamp: number
  isWinner: boolean
}

export interface RandomNumberState {
  targetNumber: number | null
  status: 'idle' | 'active' | 'finished'
  winners: RandomNumberWinner[]
  logs?: Record<string, RandomNumberLog>
  forceNextWin: boolean
  startedAt: number | null
  showQRCode?: boolean
  settings: {
    min: number
    max: number
    duration: number
    winProbability: number
    topPadding?: number
  }
}

const DEFAULT_STATE: RandomNumberState = {
  targetNumber: null,
  status: 'idle',
  winners: [],
  logs: {},
  forceNextWin: false,
  startedAt: null,
  showQRCode: true,
  settings: {
    min: 1,
    max: 100,
    duration: 2000,
    winProbability: 0, // Default to pure random
    topPadding: 32, // Default top padding of 32px (matching standard p-8)
  },
}

export function subscribeToRandomNumber(
  callback: (state: RandomNumberState) => void,
) {
  const activityRef = ref(rtdb, 'activities/random-number')
  return listenToRTDB<RandomNumberState>(
    activityRef,
    (data) => {
      callback(data || DEFAULT_STATE)
    },
    {
      onError: (error) => {
        console.error('Random number subscription error:', error)
      },
    },
  )
}

export async function initializeRandomNumberIfNeeded() {
  try {
    const activityRef = ref(rtdb, 'activities/random-number')
    const data = await getRTDB<RandomNumberState>(activityRef)
    if (!data) {
      await set(activityRef, DEFAULT_STATE)
    }
  } catch (error: any) {
    console.error('Error initializing random number game:', error)
  }
}

export async function updateRandomNumberState(
  updates: Partial<RandomNumberState>,
) {
  const activityRef = ref(rtdb, 'activities/random-number')
  await update(activityRef, updates)
}

export async function resetRandomNumberGame() {
  const activityRef = ref(rtdb, 'activities/random-number')
  await set(activityRef, DEFAULT_STATE)
}

export async function drawRandomNumber() {
  try {
    const response = await postApiActivitiesRandomNumber({
      body: {},
    })

    if (!response.data?.success) {
      throw new Error('Failed to draw random number')
    }

    return response.data
  } catch (error: any) {
    console.error('Error drawing random number:', error)
    throw error
  }
}

import type {
  WallSession,
  WallState,
  WallWinner,
} from '../api/client/types.gen'

export type { WallSession, WallState, WallWinner }

const DEFAULT_WALL_STATE: WallState = {
  isSpinning: false,
  winner: null,
  winners: [],
  currentSessionId: 'default',
  sessions: [
    {
      id: 'default',
      name: 'เซสชันที่ 1',
      createdAt: Date.now(),
      settings: {
        spinDuration: 5,
        excludeWinners: true,
        autoClose: true,
        autoCloseDelay: 10,
        displayMethod: 'bubbles',
        maritalStatusFilter: [],
        backgroundImagePath: '',
        backgroundImageUrl: '',
      },
    },
  ],
  settings: {
    spinDuration: 5,
    excludeWinners: true,
    autoClose: true,
    autoCloseDelay: 10,
    displayMethod: 'bubbles',
    maritalStatusFilter: [],
    backgroundImagePath: '',
    backgroundImageUrl: '',
  },
}

export function subscribeToWallState(callback: (state: WallState) => void) {
  const wallRef = ref(rtdb, 'activities/wall')
  return listenToRTDB<WallState>(
    wallRef,
    (data) => {
      if (!data) {
        callback(DEFAULT_WALL_STATE)
        return
      }
      // Firebase RTDB converts arrays to keyed objects — normalize
      const winners: WallWinner[] = data.winners
        ? Object.values(data.winners)
        : []

      const sessions: WallSession[] = data.sessions
        ? Object.values(data.sessions).map((s: any) => ({
            ...s,
            settings: { ...DEFAULT_WALL_STATE.settings, ...s.settings },
          }))
        : DEFAULT_WALL_STATE.sessions

      // Merge settings with defaults so new fields (e.g. maritalStatusFilter)
      // work even when the stored state predates them.
      const settings = { ...DEFAULT_WALL_STATE.settings, ...data.settings }
      callback({ ...DEFAULT_WALL_STATE, ...data, winners, sessions, settings })
    },
    {
      onError: (error) => {
        console.error('Wall state subscription error:', error)
      },
    },
  )
}

export async function getWallState() {
  const response = await getApiActivitiesWall()
  if (!response.data?.success || !response.data?.data) {
    throw new Error('Failed to get wall state')
  }
  return response.data.data as WallState
}

export async function initializeWallStateIfNeeded() {
  try {
    await getWallState()
  } catch (error: any) {
    console.error('Error initializing wall state:', error)
  }
}

export async function updateWallState(updates: Partial<WallState>) {
  const response = await postApiActivitiesWall({
    body: updates,
  })
  if (!response.data?.success) {
    throw new Error('Failed to update wall state')
  }
}

export async function resetWallState() {
  const response = await postApiActivitiesWall({
    body: DEFAULT_WALL_STATE,
  })
  if (!response.data?.success) {
    throw new Error('Failed to reset wall state')
  }
}
